import { toRem, baseVariables, Button } from '@marriott/mi-ui-library';
import styled, { css } from 'styled-components';

type EnrollmentCardProps = {
  $variation: string;
};

const isLoggedInVariantBoolean = ({ $variation }: { $variation: string }) => $variation === 'logged-in';

const isLoggedInVariant = (loggedInCase: string | number, loggedOutCase: string | number) => {
  return (props: EnrollmentCardProps) => (isLoggedInVariantBoolean(props) ? loggedInCase : loggedOutCase);
};

export const Description = styled.p.attrs((props: { $variation: string }) => ({
  className: `t-font-${isLoggedInVariantBoolean(props) ? 'inverse-s' : 's'}`,
}))`
  margin-bottom: ${isLoggedInVariant(toRem(16), toRem(26))};
  display: ${isLoggedInVariant('block', 'none')};

  @media ${baseVariables.mediaQuery.lg} {
    display: block;
  }
`;

export const Title = styled.div.attrs((props: { $variation: string }) => ({
  className: `t-title-${isLoggedInVariantBoolean(props) ? 'inverse-s' : 's'}`,
}))`
  margin-bottom: ${isLoggedInVariant(toRem(8), toRem(23))};

  @media ${baseVariables.mediaQuery.lg} {
    margin-bottom: ${isLoggedInVariant(toRem(8), toRem(12))};
  }
`;

export const CenterContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: ${toRem(23)};
  flex: 1;

  & > * {
    border-top: 1px solid ${baseVariables.color.neutral20};
    border-right: 1px solid ${baseVariables.color.neutral20};
  }

  & > *:nth-child(-n + 2) {
    border-top: none;
  }

  & > *:nth-child(even) {
    border-right: none;
  }

  @media ${baseVariables.mediaQuery.lg} {
    margin-bottom: ${toRem(26)};
  }
`;

export const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: ${toRem(20)};
`;

export const StyledButton = styled(Button)`
  flex: 1;
`;

export const EnrollmentCardContent = styled.div`
  z-index: 0; // sets base z-index, so children don't fall through when they set their own z-index.
  position: relative;
  line-height: 150%;
  display: flex;
  flex-direction: column;
  padding: ${toRem(24)};
  width: 100%;

  @media ${baseVariables.mediaQuery.lg} {
    padding-bottom: ${toRem(40)};
    padding-top: ${toRem(40)};
  }
`;

export const FloatingCardContent = styled.div`
  height: 100%;
  display: flex;
  border-radius: 14px;
  overflow: hidden;
`;

export const Card = styled.div<EnrollmentCardProps>`
  position: relative;
  margin: 0 auto;
  border-radius: ${toRem(14)};
  background: ${baseVariables.color.base20};
  text-align: center;
  width: fit-content;
  aspect-ratio: 1/1;

  img {
    transition: transform 600ms ease;
  }

  :hover img {
    transform: scale(1.165);
    overflow: hidden;
  }

  .enrollment-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: ${toRem(14)};
  }

  @media ${baseVariables.mediaQuery.lg} {
    aspect-ratio: 2/3;
  }

  ${(props: EnrollmentCardProps) => {
    if (props.$variation === 'logged-in') {
      return css`
        ${FloatingCardContent} {
          position: absolute;
          bottom: 0;
        }

        ${EnrollmentCardContent} {
          justify-content: flex-end;
          padding: 0 ${toRem(24)} ${toRem(24)};
          text-align: left;

          &::after {
            content: '';
            position: absolute;
            inset: 0;
            background: linear-gradient(
              to top,
              var(--color-scrim-100, rgba(0, 0, 0, 0.54)) 56%,
              var(--color-scrim-0, rgba(0, 0, 0, 0)) 82%
            );
            z-index: -1;
            left: 0;
            right: 0;
            bottom: 0;
          }

          a {
            text-align: center;
          }

          .inverse {
            background: none;
          }

          @media ${baseVariables.mediaQuery.lg} {
            &::after {
              background: linear-gradient(
                to top,
                var(--color-scrim-100, rgba(0, 0, 0, 0.54)) 40%,
                var(--color-scrim-0, rgba(0, 0, 0, 0)) 61%
              );
            }
          }
        }

        picture {
          width: 100%;
          aspect-ratio: inherit;
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border-radius: ${toRem(14)};
        }
      `;
    } else return '';
  }}
`;
